import {
    DESTINATION_ICON_BY_CODE,
    getDestinationIconByCode,
} from "helpers/destinations/getDestinationIcon";

import {
    DestinationCategory,
    DestinationCode,
    DestinationConfig,
    DestinationModalMode,
    TabType,
} from "./DestinationModal.types";

export const DESTINATION_ICONS: Record<DestinationCode, string> =
    DESTINATION_ICON_BY_CODE;

export const DEFAULT_DESTINATION_ICON = "";

export const DESTINATION_CATEGORIES: Record<
    DestinationCategory,
    DestinationCode[]
> = {
    meta: ["fbpixel", "fbcapi", "fboffline", "fbca"],
    tiktok: ["ttpixel", "ttcapi", "ttoffline", "ttca"],
    snapchat: ["scpixel", "sccapi"],
    pinterest: ["pnpixel", "pncapi"],
    google: ["ggads", "ggads_web", "ggawc", "ggaoc", "ggalc", "ggass"],
    twitter: ["twpixel", "twcapi"],
    reddit: ["rdpixel", "rdcapi"],
    applovin: ["alpixel"],
    microsoft: ["msoffline"],
    yandex: ["ymwc"],
    other: ["sstask", "licapi", "oaipixel", "oaicapi"],
};

export const DESTINATION_CONFIG: Record<DestinationCode, DestinationConfig> = {
    fbpixel: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "meta",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.fbpixel,
        displayName: "Meta Pixel",
        reconnectSupported: true,
    },
    fbcapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "meta",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.fbcapi,
        displayName: "Meta Conversions API",
        reconnectSupported: true,
    },
    fboffline: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "meta",
        features: {},
        icon: DESTINATION_ICONS.fboffline,
        displayName: "Meta Offline Conversions",
    },
    fbca: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "meta",
        features: {},
        icon: DESTINATION_ICONS.fbca,
        displayName: "Meta Custom Audience",
    },

    alpixel: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "applovin",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.alpixel,
        displayName: "AppLovin Pixel",
        reconnectSupported: true,
    },

    ttpixel: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "tiktok",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.ttpixel,
        displayName: "TikTok Pixel",
        reconnectSupported: true,
    },
    ttcapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "tiktok",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.ttcapi,
        displayName: "TikTok Conversions API",
        reconnectSupported: true,
    },
    ttoffline: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "tiktok",
        features: {},
        icon: DESTINATION_ICONS.ttoffline,
        displayName: "TikTok Offline Events",
    },
    ttca: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "tiktok",
        features: {},
        icon: DESTINATION_ICONS.ttca,
        displayName: "TikTok Custom Audience",
    },

    scpixel: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "snapchat",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.scpixel,
        displayName: "Snapchat Pixel",
        reconnectSupported: true,
    },
    sccapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "snapchat",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.sccapi,
        displayName: "Snapchat Conversions API",
        reconnectSupported: true,
    },

    pnpixel: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "pinterest",
        features: {},
        icon: DESTINATION_ICONS.pnpixel,
        displayName: "Pinterest Tag",
    },
    pncapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "pinterest",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.pncapi,
        displayName: "Pinterest Conversion API",
        reconnectSupported: true,
    },

    ggads: {
        trackerTabs: ["overview", "editMapping", "reconnect"],
        standaloneTabs: ["overview", "editMapping", "reconnect"],
        category: "google",
        features: {
            reconnect: true,
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggads,
        displayName: "Google Offline Conversions",
        reconnectSupported: true,
    },
    ggads_web: {
        trackerTabs: ["overview", "editMapping", "reconnect"],
        standaloneTabs: ["overview", "editMapping", "reconnect"],
        category: "google",
        features: {
            reconnect: true,
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggads_web,
        displayName: "Google Web Conversions",
        reconnectSupported: true,
    },
    ggawc: {
        trackerTabs: ["overview", "editMapping"],
        standaloneTabs: ["overview", "editMapping"],
        category: "google",
        features: {
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggawc,
        displayName: "Google Ads Website Conversion",
    },
    ggaoc: {
        trackerTabs: ["overview", "editMapping"],
        standaloneTabs: ["overview", "editMapping"],
        category: "google",
        features: {
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggaoc,
        displayName: "Google Ads Offline Conversion",
    },
    ggalc: {
        trackerTabs: ["overview", "editMapping", "reconnect"],
        standaloneTabs: ["overview", "editMapping", "reconnect"],
        category: "google",
        features: {
            reconnect: true,
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggalc,
        displayName: "Google Ads Lead Conversion",
        reconnectSupported: true,
    },
    ggass: {
        trackerTabs: ["overview", "editMapping", "reconnect"],
        standaloneTabs: ["overview", "editMapping", "reconnect"],
        category: "google",
        features: {
            reconnect: true,
            editMapping: true,
        },
        icon: DESTINATION_ICONS.ggass,
        displayName: "Google Ads Store Sales",
        reconnectSupported: true,
    },

    twpixel: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "twitter",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.twpixel,
        displayName: "X Pixel",
        reconnectSupported: true,
    },
    twcapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "twitter",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.twcapi,
        displayName: "X Conversion API",
        reconnectSupported: true,
    },

    rdpixel: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "reddit",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.rdpixel,
        displayName: "Reddit Pixel",
        reconnectSupported: true,
    },
    rdcapi: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "reddit",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.rdcapi,
        displayName: "Reddit Conversions API",
        reconnectSupported: true,
    },

    sstask: {
        trackerTabs: ["overview", "editMapping"],
        standaloneTabs: ["overview", "editMapping"],
        category: "other",
        features: {
            editMapping: true,
        },
        icon: DESTINATION_ICONS.sstask,
        displayName: "SignalSight Task",
    },
    licapi: {
        trackerTabs: ["overview", "editMapping"],
        standaloneTabs: ["overview", "editMapping"],
        category: "other",
        features: {
            editMapping: true,
        },
        icon: DESTINATION_ICONS.licapi,
        displayName: "LinkedIn Conversion API",
    },
    msoffline: {
        trackerTabs: ["overview", "editMapping"],
        standaloneTabs: ["overview", "editMapping"],
        category: "microsoft",
        features: {
            editMapping: true,
        },
        icon: DESTINATION_ICONS.msoffline,
        displayName: "Microsoft Bing Offline",
    },
    ymwc: {
        trackerTabs: ["overview", "reconnect"],
        standaloneTabs: ["overview", "reconnect"],
        category: "yandex",
        features: {
            reconnect: true,
        },
        icon: DESTINATION_ICONS.ymwc,
        displayName: "Yandex Metrica Web Conversions",
        reconnectSupported: true,
    },
    ggbq: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "google",
        features: {},
        icon: DESTINATION_ICONS.ggbq,
        displayName: "Google BigQuery",
    },
    lncapi: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "other",
        features: {},
        icon: DESTINATION_ICONS.lncapi,
        displayName: "Line Conversion API",
    },
    lnpixel: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "other",
        features: {},
        icon: DESTINATION_ICONS.lnpixel,
        displayName: "Line Pixel",
    },
    oaipixel: {
        trackerTabs: ["overview"],
        standaloneTabs: ["overview"],
        category: "other",
        features: {},
        icon: DESTINATION_ICONS.oaipixel,
        displayName: "OpenAI Ads Pixel",
    },
    oaicapi: {
        trackerTabs: ["overview", "editMapping", "reconnect"],
        standaloneTabs: ["overview", "editMapping", "reconnect"],
        category: "other",
        features: {
            reconnect: true,
            editMapping: true,
        },
        icon: DESTINATION_ICONS.oaicapi,
        displayName: "OpenAI Ads Conversions API",
        reconnectSupported: true,
    },
};

export const TAB_LABELS: Record<TabType, { key: string; default: string }> = {
    overview: { key: "buttons.overview", default: "Overview" },
    reconnect: { key: "buttons.reconnect", default: "Reconnect" },
    editMapping: { key: "buttons.edit_mapping", default: "Edit Mapping" },
};

export const getDestinationIcon = (
    code: DestinationCode | string | undefined,
): string => {
    if (!code) return DEFAULT_DESTINATION_ICON;

    return getDestinationIconByCode(code) || DEFAULT_DESTINATION_ICON;
};

export const getDestinationConfig = (
    code: DestinationCode | string | undefined,
): DestinationConfig | null => {
    if (!code) return null;
    return DESTINATION_CONFIG[code as DestinationCode] || null;
};

export const getDestinationTabs = (
    code: DestinationCode | string | undefined,
    mode?: DestinationModalMode,
): TabType[] => {
    const config = getDestinationConfig(code);
    if (mode === "standalone") {
        return config?.standaloneTabs || ["overview"];
    }
    return config?.trackerTabs || ["overview"];
};

export const hasFeature = (
    code: DestinationCode | string | undefined,
    feature: keyof DestinationConfig["features"],
): boolean => {
    const config = getDestinationConfig(code);
    return config?.features?.[feature] || false;
};

export const hasReconnectSupport = (
    code: DestinationCode | string | undefined,
): boolean => {
    const config = getDestinationConfig(code);
    return config?.reconnectSupported || false;
};

export const getDestinationCategory = (
    code: DestinationCode | string | undefined,
): DestinationCategory | null => {
    const config = getDestinationConfig(code);
    return config?.category || null;
};

export const getDestinationDisplayName = (
    code: DestinationCode | string | undefined,
    fallback?: string,
): string => {
    const config = getDestinationConfig(code);
    return config?.displayName || fallback || code || "Unknown Destination";
};

export const RECONNECT_SUPPORTED_CODES: DestinationCode[] = [
    "fbcapi",
    "fbpixel",
    "alpixel",
    "ttcapi",
    "ttpixel",
    "scpixel",
    "sccapi",
    "ggads",
    "ggads_web",
    "ggalc",
    "ggass",
    "twpixel",
    "twcapi",
    "pncapi",
    "rdcapi",
    "rdpixel",
    "oaicapi",
    "ymwc",
];

export const EDIT_MAPPING_SUPPORTED_CODES: DestinationCode[] = [
    "ggads",
    "ggads_web",
    "ggalc",
    "ggass",
    "sstask",
    "licapi",
    "msoffline",
    "oaicapi",
];
